/**
 * The client ledger and its statement — FR-G1, T-3.9. Redrawn on the list
 * shell in Wave 15D (C14 §10.6 items 1–2, ADR-0033; T-6.16): the client and the
 * date range in the toolbar, the balances as headline figures, the entries as
 * the kit's table with search, a kind chip, paging and Export.
 *
 * **Debit and credit as two columns, with a running balance**, because that is
 * what a statement of account looks like in India and what a firm reconciles
 * against its own books. A single signed column is shorter and nobody can read
 * it out loud. The entries are **not sortable**: a running balance read in any
 * order but the ledger's own is a column of numbers that mean nothing.
 *
 * **A memo row moves nothing**, and the screen says so rather than leaving a
 * reader to work out why the balance did not change: an advance being recorded
 * and an advance being applied are real events on a client's account that carry
 * no money of their own, because the receipt and the invoice already did
 * (`app/models/billing.py`).
 *
 * **The CSV is the statement** (FR-G1) — the server's, opening and closing
 * lines and all — and it stays the page's primary action. A statement is a
 * working document a firm opens in a spreadsheet; the invoice is the one that
 * must look like a document, and that one is a PDF. *Export* beside the search
 * is the Wave 15 one: the entries the search and chip left, as Excel.
 */

import { Badge, Button, Input, Select } from '@fiveox/ui'
import { Download } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import {
  ExportButton,
  type ListColumn,
  ListPage,
  ListTable,
  ListToolbar,
  useListTable,
} from '../../components/page'
import { ApiError } from '../../lib/api'
import {
  KIND_LABELS,
  type LedgerEntry,
  type Statement,
  balanceLabel,
  billing,
  financialYearToDate,
  pdfUrl,
  rupees,
} from '../../lib/api/billing'
import type { ClientRecord } from '../../lib/api/clients'
import { Money, activeClients } from './shared'

function Headline({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div
      data-headline={label}
      className="border-border min-w-0 rounded-2xl border bg-card p-4 shadow-sm"
    >
      <div className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
        {label}
      </div>
      <div
        className={`mt-1 text-lg font-semibold break-words tabular-nums sm:text-xl ${
          muted ? 'text-muted-foreground' : 'text-foreground'
        }`}
      >
        {value}
      </div>
    </div>
  )
}

export function Ledger() {
  const navigate = useNavigate()
  const [book, setBook] = useState<ClientRecord[]>([])
  const [booked, setBooked] = useState(false)
  const [client, setClient] = useState('')
  const [range, setRange] = useState(financialYearToDate())
  const [statement, setStatement] = useState<Statement | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    void activeClients()
      .then((rows) => {
        setBook(rows)
        // Land on the first client rather than on an empty screen with a
        // dropdown: a ledger with nothing chosen teaches nothing about what the
        // screen is for.
        setClient((current) => current || (rows[0]?.id ?? ''))
        if (rows.length === 0) setLoading(false)
      })
      .catch(() => {
        setError('The clients could not be loaded.')
        setLoading(false)
      })
      .finally(() => setBooked(true))
  }, [])

  const load = useCallback(async () => {
    if (!client) return
    setLoading(true)
    setError(null)
    try {
      const resp = await billing.statement(client, range.from, range.to)
      setStatement(resp.data)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That statement could not be loaded.')
      setStatement(null)
    } finally {
      setLoading(false)
    }
  }, [client, range.from, range.to])

  useEffect(() => {
    void load()
  }, [load])

  const download = async () => {
    if (!client) return
    setError(null)
    try {
      // The same authenticated fetch the PDFs use: the route is org-scoped, so
      // a plain link would download a 401.
      const url = await pdfUrl(
        `/api/v1/billing/clients/${client}/statement.csv?from=${range.from}&to=${range.to}`,
      )
      const link = document.createElement('a')
      link.href = url
      link.download = `statement-${range.from}-${range.to}.csv`
      link.click()
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That statement could not be downloaded.')
    }
  }

  const entries = useMemo(() => statement?.entries ?? [], [statement])
  const kinds = [...new Set(entries.map((e) => e.kind))]

  const columns = useMemo<ListColumn<LedgerEntry>[]>(
    () => [
      {
        key: 'entry_date',
        label: 'Date',
        width: 110,
        required: true,
        render: (entry) => (
          <span className="text-muted-foreground whitespace-nowrap">{entry.entry_date}</span>
        ),
        exportValue: (entry) => entry.entry_date,
      },
      {
        key: 'particulars',
        label: 'Particulars',
        width: 260,
        phone: true,
        render: (entry) => (
          <span className="block max-w-[12rem] min-w-0 whitespace-normal sm:max-w-md">
            <span className="block break-words">{entry.narration}</span>
            <span className="mt-0.5 flex flex-wrap items-center gap-2">
              <Badge tone={entry.direction === 'memo' ? 'neutral' : 'info'}>
                {KIND_LABELS[entry.kind] ?? entry.kind}
              </Badge>
              {entry.direction === 'memo' && (
                <span className="text-muted-foreground text-xs">does not change the balance</span>
              )}
              <span className="text-muted-foreground text-xs sm:hidden">{entry.entry_date}</span>
            </span>
            {/* On a phone the debit and credit columns are gone; the movement
                rides here, beside the running balance. */}
            {(entry.debit || entry.credit) && (
              <span className="text-muted-foreground mt-0.5 block text-xs tabular-nums sm:hidden">
                {entry.debit ? `Debit ${rupees(entry.debit)}` : `Credit ${rupees(entry.credit)}`}
              </span>
            )}
          </span>
        ),
        exportValue: (entry) => entry.narration,
      },
      {
        key: 'kind',
        label: 'Kind',
        width: 0,
        // In the sheet only: on screen it is the badge under the particulars.
        render: () => null,
        exportValue: (entry) => KIND_LABELS[entry.kind] ?? entry.kind,
      },
      {
        key: 'debit',
        label: 'Debit',
        width: 110,
        align: 'right',
        render: (entry) => (entry.debit ? <Money value={entry.debit} /> : ''),
        exportValue: (entry) => entry.debit ?? '',
      },
      {
        key: 'credit',
        label: 'Credit',
        width: 110,
        align: 'right',
        render: (entry) => (entry.credit ? <Money value={entry.credit} /> : ''),
        exportValue: (entry) => entry.credit ?? '',
      },
      {
        key: 'balance',
        label: 'Balance',
        width: 120,
        align: 'right',
        phone: true,
        render: (entry) => <Money value={entry.running_balance} />,
        exportValue: (entry) => entry.running_balance ?? '',
      },
    ],
    [],
  )
  const shown = useMemo(() => columns.filter((c) => c.key !== 'kind'), [columns])

  const list = useListTable(entries, {
    columns: shown,
    search: (entry) => [entry.narration, KIND_LABELS[entry.kind]],
    filters: [
      {
        key: 'kind',
        label: 'Kind',
        allLabel: 'Every kind',
        options: kinds.map((kind) => ({ value: kind, label: KIND_LABELS[kind] ?? kind })),
        match: (entry, value) => entry.kind === value,
      },
    ],
    pageSize: 50,
  })

  const balance = statement ? balanceLabel(statement.closing) : null
  const chosen = book.find((c) => c.id === client)

  const csv = (
    <span title={statement ? undefined : 'Choose a client whose statement has loaded.'}>
      <Button variant="outline" onClick={() => void download()} disabled={!statement}>
        <Download size={16} aria-hidden /> Download CSV
      </Button>
    </span>
  )

  return (
    <ListPage
      title="Statement"
      description={
        chosen
          ? `${chosen.legal_name} — every invoice, receipt, credit note and advance on the account.`
          : 'Every invoice, receipt, credit note and advance on one client’s account.'
      }
      primary={book.length > 0 ? csv : undefined}
      loading={loading && entries.length === 0}
      error={error}
      // Nothing to show a statement *of*: no clients at all. A client whose
      // account is empty in the range is the table's own empty line, and the
      // client and dates stay in reach to change.
      isEmpty={booked && book.length === 0}
      empty={{
        title: 'No clients yet',
        description:
          'A statement is one client’s account. Add a client, raise an invoice or record a receipt, and it appears here.',
        action: <Button onClick={() => navigate('/clients')}>Go to your clients</Button>,
      }}
      toolbar={
        <ListToolbar
          list={list}
          placeholder="Search the particulars"
          actions={
            <ExportButton
              rows={list.sorted}
              columns={columns}
              filename={`prodesk-statement-${range.from}-${range.to}`}
            />
          }
        >
          <Select
            size="sm"
            aria-label="Client"
            className="w-full sm:w-56"
            value={client}
            onChange={(e) => setClient(e.target.value)}
          >
            {book.map((c) => (
              <option key={c.id} value={c.id}>
                {c.legal_name}
              </option>
            ))}
          </Select>
          <div className="w-[calc(50vw-2rem)] sm:w-40">
            <Input
              size="sm"
              type="date"
              aria-label="From"
              value={range.from}
              onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
            />
          </div>
          <div className="w-[calc(50vw-2rem)] sm:w-40">
            <Input
              size="sm"
              type="date"
              aria-label="To"
              value={range.to}
              onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
            />
          </div>
        </ListToolbar>
      }
    >
      {statement && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5" data-statement-figures="">
          <Headline label="Opening balance" value={rupees(statement.opening)} muted />
          <Headline label="Debits" value={rupees(statement.total_debit)} />
          <Headline label="Credits" value={rupees(statement.total_credit)} />
          <Headline
            label="Closing balance"
            value={balance?.text ?? '—'}
            muted={balance?.tone !== 'owed'}
          />
          {statement.advance_held !== '0.00' && (
            <Headline label="Advance held" value={rupees(statement.advance_held)} />
          )}
        </div>
      )}
      <ListTable
        list={list}
        rowKey={(entry) => entry.id}
        loading={loading}
        empty={
          statement ? (
            <span className="text-muted-foreground text-sm">
              Nothing on this account between {statement.from} and {statement.to}.
            </span>
          ) : undefined
        }
      />
    </ListPage>
  )
}
